import { useEffect } from 'react';

import * as Sentry from '@sentry/react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from 'hooks/useAppSelector';

/**
 * Attaches only non-PII identifiers to Sentry events, replacing the data that
 * used to be auto-collected via `sendDefaultPii`. Everything sent here is an
 * internal id, enum or static route path — never names, email, phone, SSN, DOB,
 * addresses or tokens. Clears itself on logout (state reset -> ids become null).
 */
export const useSentryContext = () => {
  const borrowerId = useAppSelector((state) => state.borrower.borrowerId);
  const applicationId = useAppSelector((state) => state.application.applicationId);
  const intermediaryId = useAppSelector((state) => state.application.intermediaryId);
  const { currentPreQualificationStep, currentSignInStep, currentDashboardStep, currentContractorPortalStep } =
    useAppSelector((state) => state.stages);
  const { pathname } = useLocation();

  useEffect(() => {
    Sentry.setUser(borrowerId ? { id: borrowerId } : null);
  }, [borrowerId]);

  useEffect(() => {
    Sentry.setTag('applicationId', applicationId ?? null);
    Sentry.setTag('intermediaryId', intermediaryId ?? null);
  }, [applicationId, intermediaryId]);

  useEffect(() => {
    Sentry.setContext('funnel', {
      route: pathname,
      preQualificationStep: currentPreQualificationStep ?? null,
      signInStep: currentSignInStep ?? null,
      dashboardStep: currentDashboardStep ?? null,
      contractorPortalStep: currentContractorPortalStep ?? null,
    });
  }, [pathname, currentPreQualificationStep, currentSignInStep, currentDashboardStep, currentContractorPortalStep]);
};
